import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AbstractStateInterface } from '../../interface/abstract';
import { VoucherInterface } from '../../interface/voucher';
import { ApiService } from '../../utils/apiService';

type DataState = AbstractStateInterface<VoucherInterface>;

const initialState: DataState = {
  data: [],
  detail: null,
  loading: false,
};

export const getAllVoucherList = createAsyncThunk('/admin-api/voucher/list', async (_, thunkAPI) => {
  try {
    const response = await ApiService(`/admin-api/voucher/list`, 'GET', null);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const findVoucherById = createAsyncThunk('/admin-api/voucher', async (payload: { id: string }, thunkAPI) => {
  const { id } = payload;
  try {
    const response = await ApiService(`/admin-api/voucher/${id}`, 'GET', null);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const createVoucher = createAsyncThunk(
  '/admin-api/voucher/create-voucher',
  async (payload: FormData, thunkAPI) => {
    try {
      const response = await ApiService(`/admin-api/voucher/create-voucher`, 'POST', payload);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

export const updateVoucher = createAsyncThunk(
  '/admin-api/voucher/update-voucher/put',
  async (payload: FormData, thunkAPI) => {
    try {
      const response = await ApiService(`/admin-api/voucher/update-voucher`, 'PUT', payload);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

export const deleteVoucher = createAsyncThunk('/admin-api/voucher', async (id: string, thunkAPI) => {
  try {
    const response = await ApiService(`/admin-api/voucher/${id}`, 'DELETE', null);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

const voucherSlice = createSlice({
  name: 'voucher',
  initialState,
  reducers: {
    resetVoucherSlice: () => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllVoucherList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllVoucherList.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload.data)) {
          state.data = action.payload.data as VoucherInterface[];
        } else {
          state.data = [];
        }
      })
      .addCase(findVoucherById.pending, (state) => {
        state.loading = true;
      })
      .addCase(findVoucherById.fulfilled, (state, action) => {
        state.detail = action.payload.data as VoucherInterface;
      })
      .addCase(findVoucherById.rejected, (state, action) => {
        state.loading = false;
      });
  },
});

export const { resetVoucherSlice } = voucherSlice.actions;
export default voucherSlice.reducer;
